import { useState } from "react";
import { PageHeader } from "../shell/PageHeader";
import { useCapabilities } from "../session/capabilities";

type TokenGroup = {
  title: string;
  tokens: { key: string; label: string; type: "color" | "size" | "font" | "shadow" }[];
};

const TOKEN_GROUPS: TokenGroup[] = [
  {
    title: "Accent",
    tokens: [
      { key: "--sk-accent", label: "Accent", type: "color" },
      { key: "--sk-accent-contrast", label: "Accent contrast", type: "color" },
      { key: "--sk-color-accent", label: "UI accent", type: "color" },
      { key: "--sk-color-accent-soft", label: "Accent soft", type: "color" },
    ],
  },
  {
    title: "Surfaces",
    tokens: [
      { key: "--sk-bg", label: "Background", type: "color" },
      { key: "--sk-surface", label: "Surface", type: "color" },
      { key: "--sk-surface-raised", label: "Surface raised", type: "color" },
      { key: "--sk-color-surface-0", label: "Surface 0", type: "color" },
      { key: "--sk-color-surface-1", label: "Surface 1", type: "color" },
      { key: "--sk-color-surface-2", label: "Surface 2", type: "color" },
    ],
  },
  {
    title: "Text",
    tokens: [
      { key: "--sk-text", label: "Text primary", type: "color" },
      { key: "--sk-text-muted", label: "Text muted", type: "color" },
      { key: "--sk-color-ink-1", label: "Ink 1", type: "color" },
      { key: "--sk-color-ink-2", label: "Ink 2", type: "color" },
      { key: "--sk-color-ink-3", label: "Ink 3", type: "color" },
    ],
  },
  {
    title: "Borders",
    tokens: [
      { key: "--sk-border", label: "Border", type: "color" },
      { key: "--sk-color-border-1", label: "Border 1", type: "color" },
      { key: "--sk-color-border-2", label: "Border 2", type: "color" },
    ],
  },
  {
    title: "Equipment health",
    tokens: [
      { key: "--sk-health-ok", label: "OK", type: "color" },
      { key: "--sk-health-warning", label: "Warning", type: "color" },
      { key: "--sk-health-fault", label: "Fault", type: "color" },
      { key: "--sk-health-offline", label: "Offline", type: "color" },
      { key: "--sk-health-unmonitored", label: "Unmonitored", type: "color" },
    ],
  },
  {
    title: "Source palette",
    tokens: [
      { key: "--sk-color-source-solar", label: "Solar", type: "color" },
      { key: "--sk-color-source-wind", label: "Wind", type: "color" },
      { key: "--sk-color-source-storage", label: "Storage", type: "color" },
      { key: "--sk-color-source-grid", label: "Grid", type: "color" },
      { key: "--sk-color-source-diesel", label: "Diesel", type: "color" },
      { key: "--sk-color-source-load", label: "Load", type: "color" },
    ],
  },
  {
    title: "Typography",
    tokens: [
      { key: "--sk-font-text", label: "Text family", type: "font" },
      { key: "--sk-font-mono", label: "Mono family", type: "font" },
    ],
  },
  {
    title: "Spacing and radii",
    tokens: [
      { key: "--sk-radius-card", label: "Card radius", type: "size" },
      { key: "--sk-radius-control", label: "Control radius", type: "size" },
      { key: "--sk-radius-chip", label: "Chip radius", type: "size" },
      { key: "--sk-density-gap", label: "Density gap", type: "size" },
      { key: "--sk-density-pad", label: "Density pad", type: "size" },
      { key: "--sk-density-row-h", label: "Row height", type: "size" },
    ],
  },
  {
    title: "Elevation",
    tokens: [
      { key: "--sk-shadow-card", label: "Card shadow", type: "shadow" },
      { key: "--sk-shadow-float", label: "Float shadow", type: "shadow" },
    ],
  },
];

export function Branding({ tenant }: { tenant: string }): React.ReactNode {
  const caps = useCapabilities();
  const adminFamily = caps.rail_families.find((f) => f.name === "admin");
  const canEdit = adminFamily?.visible === true && adminFamily?.locked === false;

  const [activeGroup, setActiveGroup] = useState(0);

  return (
    <div className="sk-branding">
      <PageHeader
        title="Branding"
        scope={`Tenant token override for white-label · ${tenant}`}
        showTimeControl={false}
      />

      <div className="sk-branding__status-bar">
        <span className="sk-branding__status-badge">WIRED</span>
        <span className="sk-branding__status-text">
          Design-in — tokens are live from the design system; per-tenant override API is not yet published (seam 13).
        </span>
      </div>

      <div className="sk-branding__layout">
        {/* Left: group tabs */}
        <nav className="sk-branding__nav" aria-label="Token groups">
          {TOKEN_GROUPS.map((group, i) => (
            <button
              key={group.title}
              type="button"
              className={`sk-branding__nav-item ${i === activeGroup ? "sk-branding__nav-item--active" : ""}`}
              onClick={() => setActiveGroup(i)}
              aria-current={i === activeGroup ? "true" : undefined}
            >
              {group.title}
              <span className="sk-branding__nav-count">{group.tokens.length}</span>
            </button>
          ))}
        </nav>

        {/* Right: token table */}
        <section className="sk-branding__panel" aria-labelledby="branding-group-title">
          <h2 id="branding-group-title" className="sk-branding__group-title">
            {TOKEN_GROUPS[activeGroup]?.title ?? ""}
          </h2>
          <div className="sk-table-wrap">
            <table className="sk-branding__table" role="grid" aria-label={`${TOKEN_GROUPS[activeGroup]?.title ?? ""} tokens`}>
              <thead>
                <tr>
                  <th>Token</th>
                  <th>Label</th>
                  <th>Current value</th>
                  {canEdit ? <th><span className="sr-only">Override</span></th> : null}
                </tr>
              </thead>
              <tbody>
                {(TOKEN_GROUPS[activeGroup]?.tokens ?? []).map((token) => (
                  <TokenRow key={token.key} token={token} canEdit={canEdit} />
                ))}
              </tbody>
            </table>
          </div>
          {canEdit ? (
            <p className="sk-branding__hint">
              Per-tenant overrides will be saved to seam 13 (white_label_token) once the API is published.
              For now, tokens are read from the design system.
            </p>
          ) : null}
        </section>
      </div>

      {/* Live preview strip */}
      <section className="sk-branding__preview" aria-labelledby="branding-preview-title">
        <h2 id="branding-preview-title" className="sk-branding__group-title">Live preview</h2>
        <div className="sk-branding__preview-cards">
          <div className="sk-branding__preview-card">
            <div className="sk-branding__swatch-row">
              <span className="sk-branding__swatch" style={{ background: "var(--sk-accent)" }} />
              <span className="sk-branding__swatch" style={{ background: "var(--sk-health-ok)" }} />
              <span className="sk-branding__swatch" style={{ background: "var(--sk-health-warning)" }} />
              <span className="sk-branding__swatch" style={{ background: "var(--sk-health-fault)" }} />
              <span className="sk-branding__swatch" style={{ background: "var(--sk-health-offline)" }} />
            </div>
            <span className="sk-branding__preview-label">Accent + health palette</span>
          </div>
          <div className="sk-branding__preview-card">
            <span style={{ fontFamily: "var(--sk-font-text)", fontSize: "var(--sk-type-title-size)", fontWeight: 600 }}>
              Heading text
            </span>
            <span style={{ fontFamily: "var(--sk-font-mono)", fontSize: "var(--sk-type-body-size)" }}>
              12,345.67 kWh
            </span>
            <span className="sk-branding__preview-label">Typography</span>
          </div>
          <div className="sk-branding__preview-card">
            <div className="sk-branding__surface-stack">
              <span className="sk-branding__surface-chip" style={{ background: "var(--sk-color-surface-0)" }}>S0</span>
              <span className="sk-branding__surface-chip" style={{ background: "var(--sk-color-surface-1)" }}>S1</span>
              <span className="sk-branding__surface-chip" style={{ background: "var(--sk-color-surface-2)" }}>S2</span>
            </div>
            <span className="sk-branding__preview-label">Surface layers</span>
          </div>
          <div className="sk-branding__preview-card">
            <div className="sk-branding__swatch-row">
              <span className="sk-branding__swatch" style={{ background: "var(--sk-color-source-solar)" }} />
              <span className="sk-branding__swatch" style={{ background: "var(--sk-color-source-wind)" }} />
              <span className="sk-branding__swatch" style={{ background: "var(--sk-color-source-storage)" }} />
              <span className="sk-branding__swatch" style={{ background: "var(--sk-color-source-grid)" }} />
            </div>
            <span className="sk-branding__preview-label">Source palette</span>
          </div>
        </div>
      </section>
    </div>
  );
}

function TokenRow({ token, canEdit }: { token: TokenGroup["tokens"][number]; canEdit: boolean }): React.ReactNode {
  const computed = typeof window !== "undefined"
    ? getComputedStyle(document.documentElement).getPropertyValue(token.key).trim()
    : "";

  return (
    <tr>
      <td className="sk-mono sk-branding__token-key">{token.key}</td>
      <td>{token.label}</td>
      <td>
        <span className="sk-branding__value-cell">
          {token.type === "color" ? (
            <span
              className="sk-branding__color-swatch"
              style={{ backgroundColor: `var(${token.key})` }}
              aria-hidden="true"
            />
          ) : null}
          <code className="sk-branding__value-code">{computed || "—"}</code>
        </span>
      </td>
      {canEdit ? (
        <td className="sk-branding__override-cell">
          <button type="button" className="sk-action-btn" disabled>
            Override
          </button>
        </td>
      ) : null}
    </tr>
  );
}
